import BudgetManager from '@/components/budget/BudgetManager';
import { SmartBudgetAssistant } from '@/components/onboarding/assistant/SmartBudgetAssistant';
import { AiBudgetSuggestions } from '@/components/budgets/AiBudgetSuggestions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useState, useCallback } from 'react';
import { Plus, Filter, Search, ChevronDown, Clock, Brain, Sparkles } from 'lucide-react';
import { BudgetPeriod } from '@flows.cash/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Budget() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<BudgetPeriod>('monthly');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSmartAssistantOpen, setIsSmartAssistantOpen] = useState(false);
  const [isAiSuggestionsOpen, setIsAiSuggestionsOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');
  
  // Filter handler for the budget items
  const handleFilter = useCallback((period: BudgetPeriod) => {
    setSelectedPeriod(period);
    setShowFilterOptions(false);
  }, []);

  // Search handler for filtering budgets by name
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Create budget handler - trigger the BudgetManager's own modal
  const handleCreateBudget = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  // Smart Assistant handlers
  const handleOpenSmartAssistant = useCallback(() => {
    setIsSmartAssistantOpen(true);
  }, []);

  const handleCloseSmartAssistant = useCallback(() => {
    setIsSmartAssistantOpen(false);
  }, []);

  const handleSmartAssistantComplete = useCallback(() => {
    setIsSmartAssistantOpen(false);
    // Optionally refresh the budget data here if needed
  }, []);

  // Filter dropdown content
  const filterDropdown = (
    <DropdownMenu open={showFilterOptions} onOpenChange={setShowFilterOptions}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={isMobile ? "sm" : "default"}
          className="flex-1 sm:flex-none bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm"
        >
          <Filter className={isMobile ? "w-3.5 h-3.5 mr-1.5" : "w-4 h-4 mr-2"} />
          Filters
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[180px]">
        <DropdownMenuItem onClick={() => handleFilter('daily')}>
          Daily
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleFilter('weekly')}>
          Weekly
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleFilter('monthly')}>
          Monthly
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleFilter('yearly')}>
          Yearly
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="flex-1 overflow-hidden bg-background-primary dark:bg-background-dark-primary">
      <div className="h-full flex flex-col p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex-shrink-0 border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 rounded-2xl shadow-sm">
          <div className="px-4 sm:px-8 py-5 sm:py-6">
            {/* Title and Description */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex-shrink-0">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
                  Budget
                </h1>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                  Create and manage your monthly budgets
                </p>
              </div>
              
              {/* Desktop - Action Buttons */}
              <div className="hidden sm:flex sm:items-center sm:gap-3">
                <Button
                  variant="outline"
                  className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950/50 shadow-sm rounded-xl"
                  onClick={() => setIsAiSuggestionsOpen(true)}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Suggest from my spending
                </Button>
                <Button
                  variant="outline"
                  className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950/50 shadow-sm rounded-xl"
                  onClick={handleOpenSmartAssistant}
                >
                  <Brain className="w-4 h-4 mr-2" />
                  Smart Assistant
                </Button>
                <Button
                  variant="default"
                  className="shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90"
                  onClick={handleCreateBudget}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Budget
                </Button>
              </div>
            </div>
            
            {/* Action Buttons and Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 order-2 sm:order-1 sm:flex-nowrap">
                <Button
                  variant="outline"
                  size={isMobile ? "sm" : "default"}
                  className="flex-1 sm:flex-none bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl shadow-sm"
                >
                  <Clock className={isMobile ? "w-3.5 h-3.5 mr-1.5" : "w-4 h-4 mr-2"} />
                  {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)}
                </Button>
                
                {filterDropdown}
                
                {/* Mobile only AI suggestions button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 sm:hidden border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950/50 rounded-xl"
                  onClick={() => setIsAiSuggestionsOpen(true)}
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                  Suggest
                </Button>

                {/* Mobile only Smart Assistant button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 sm:hidden border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950/50 rounded-xl"
                  onClick={handleOpenSmartAssistant}
                >
                  <Brain className="w-3.5 h-3.5 mr-1.5" />
                  Smart Assistant
                </Button>
                
                {/* Mobile only Create Budget button */}
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1 sm:hidden shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90"
                  onClick={handleCreateBudget}
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Create Budget
                </Button>
              </div>
              
              {/* Search Bar */}
              <div className="relative flex-1 order-1 sm:order-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                <Input
                  placeholder="Search budgets..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 h-10 w-full bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-border dark:border-border-dark rounded-xl shadow-sm text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pass search query and selected period to BudgetManager */}
        <BudgetManager 
          hideHeader={true}
          searchQuery={searchQuery}
          periodFilter={selectedPeriod}
          isAddModalOpen={isAddModalOpen}
          onAddModalOpenChange={setIsAddModalOpen}
          onOpenSmartAssistant={handleOpenSmartAssistant}
        />

        {/* Smart Budget Assistant Modal */}
        <SmartBudgetAssistant
          isOpen={isSmartAssistantOpen}
          onClose={handleCloseSmartAssistant}
          onComplete={handleSmartAssistantComplete}
        />

        {/* AI budget suggestions from real spending history */}
        <AiBudgetSuggestions
          open={isAiSuggestionsOpen}
          onClose={() => setIsAiSuggestionsOpen(false)}
        />
      </div>
    </div>
  );
}